import { useCallback, useMemo } from 'react';
import {
  GatewayLocationSetting,
  LocationSettingsOperation,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { useQueryClient } from 'react-query';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';

const DEFAULT_AVERAGE_PRODUCTION_AMOUNT = 200;

export const useAverageProductionAmount = () => {
  const alert = useAlert();
  const { t } = useTranslation();
  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const queryClient = useQueryClient();

  // We use groupId/ParentId to read/write average production amount value
  // So it can be available across all locations in the group
  const groupLocationId = useMemo(() => {
    return selectedLocationIds.length > 0 ? accessibleLocationData?.[selectedLocationIds[0]]?.groupId ?? null : null;
  }, [accessibleLocationData, selectedLocationIds]);

  const {
    data,
    isLoading: isFetchingAmount,
    queryKey,
  } = AnalyticsGatewayV1.Queries.useLocationSettingsQuery({
    request: {
      locationId: groupLocationId ? [groupLocationId] : [],
      settingsKey: 'avg_production_amount',
      operation: LocationSettingsOperation.FETCH,
    },
    options: {
      enabled: !!groupLocationId,
    },
  });

  const averageProductionAmount = useMemo(() => {
    // As we are saving production amount per group, we expect only one item in the response
    const item = data?.items?.[0];
    const isUserSetValue = item?.settingsValue !== undefined && item?.settingsValue !== null;

    return {
      value: item?.settingsValue ? parseInt(item.settingsValue, 10) : DEFAULT_AVERAGE_PRODUCTION_AMOUNT,
      isUserSetValue,
    };
  }, [data]);

  const options = (successAction: string, failedAction: string) => ({
    onSuccess: () => {
      alert.success(t('The average appointment value has been successfully {{action}}', { action: successAction }));
      queryClient.invalidateQueries(queryKey);
    },
    onError: () => {
      alert.error({
        message: t('Failed to {{action}} average appointment value. Please try again.', { action: failedAction }),
      });
    },
  });

  const { isLoading: isCreating, mutateAsync: createSettings } =
    AnalyticsGatewayV1.Mutations.useLocationSettingsMutation({
      options: options(t('saved'), t('save')),
    });

  const { isLoading: isUpdating, mutateAsync: updateSettings } =
    AnalyticsGatewayV1.Mutations.useModifyLocationSettingsMutation({
      options: options(t('updated'), t('update')),
    });

  const { isLoading: isDeleting, mutateAsync: deleteSettings } =
    AnalyticsGatewayV1.Mutations.useLocationSettingsMutation({
      options: options(t('deleted'), t('delete')),
    });

  const updateAverageProductionAmount = useCallback(
    async (newAmount: number) => {
      if (!groupLocationId) {
        return;
      }

      const items: GatewayLocationSetting[] = [
        {
          locationId: groupLocationId,
          settingsKey: 'avg_production_amount',
          settingsValue: newAmount.toString(),
        },
      ];

      // If user has already set a value, we perform an update, otherwise we create a new setting
      if (averageProductionAmount.isUserSetValue) {
        await updateSettings({
          operation: LocationSettingsOperation.MODIFY_SETTINGS,
          ...items[0],
          locationId: [groupLocationId],
        });
      } else {
        await createSettings({
          operation: LocationSettingsOperation.CREATE,
          items,
        });
      }
    },
    [groupLocationId, averageProductionAmount.isUserSetValue, updateSettings, createSettings]
  );

  const deleteAverageProductionAmount = useCallback(async () => {
    if (!groupLocationId || !averageProductionAmount.isUserSetValue) {
      return;
    }

    await deleteSettings({
      operation: LocationSettingsOperation.BULK_DELETE,
      locationId: [groupLocationId],
      settingsKey: 'avg_production_amount',
    });
  }, [averageProductionAmount.isUserSetValue, deleteSettings, groupLocationId]);

  return {
    averageProductionAmount,
    isLoading: isFetchingAmount || isCreating || isUpdating || isDeleting,
    updateAverageProductionAmount,
    deleteAverageProductionAmount,
  };
};
